import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import LikedSvg from '../other/svg/LikedSvg'
import { LayoutThreeColumnSvg, LayoutTwoColumnSvg } from '../other/svg/Index'
import track from 'src/helpers/track'
import { SET_COLUMN_NUMBER, SET_PAGINATION_TYPE } from 'src/redux/layoutReducer'
import PaginationSvg from '../other/svg/PaginationSvg'
import { useDispatch, useSelector } from 'react-redux'
import { RootReducer } from 'src/redux/rootReducer'
import { COLORS } from 'src/theme'
import { Link } from 'react-router-dom'
import BuyNowToggleSwitch from './BuyNowToggleSwitch'
import MotorbikeFilter from './MotorbikeFilter'
import CamperFilter from './CamperFilter'
import AgriculturalFilter from './AgriculturalFilter'
import UniversalSearch from './UniversalSearch'
import CountrySelector from './CountrySelector'
import { FETCH_OFFERS, TOGGLE_FILTER_MODAL } from 'dealer-reducers'
import { FETCH_OFFERS as FETCH_OFFERS_PAGINATED } from 'src/redux/paginatedOffersReducer'
import RefreshSvg from '../other/svg/RefreshSvg'
import Icon from './Icon'
import FilterSvg from '../other/svg/FilterSvg'
import JetSkiFilter from './JetSkiFilter'

interface Props {
  name: string
  withFiler: boolean
}

const NewToolbar = ({ name, withFiler }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { paginationType } = useSelector((state: RootReducer) => state.layoutReducer)
  const { columns } = useSelector((state: RootReducer) => state.layoutReducer)
  const currentTab = useSelector((state: RootReducer) => state.offersListReducer.tab)
  const isLoading = useSelector((state: RootReducer) => state.loaderReducer.isLoading)
  const showFilterModal = useSelector((state: RootReducer) => state.tableToolsReducer.showFilterModal)

  const activeModalFiltersCount = useSelector((state: RootReducer) => {
    const modalFilters = Object.entries(state.tableToolsReducer.filters[currentTab])
      .filter(([key, value]) => {
        if (['vehicle_type', 'hash_id', 'country_code', 'buyNowFiler', 'tab'].includes(key)) return false
        return true
      })
      .map(([_, value]) => value)

    return Object.values(modalFilters).filter((item) => item.toString().length > 0).length
  })
  const dispatch = useDispatch()

  useEffect(() => {
    if (showFilterModal) {
      dispatch(TOGGLE_FILTER_MODAL())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  return (
    <div>
      <div className="flex flex-col justify-start items-start">
        <div
          className="self-stretch bg-gradient-to-l bg-Estigroup-primary rounded-tl-lg rounded-tr-lg border-l border-r border-t border-neutral-200 flex flex-col justify-start items-center"
          style={{ borderRadius: withFiler ? '' : '8px' }}>
          <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-2 overflow-hidden">
            <div className="flex-1 flex justify-between items-center">
              <div className="flex justify-start items-center gap-3">
                <div className="flex justify-start items-center gap-1">
                  <div className="flex justify-center items-center gap-2">
                    <div className="justify-start text-xl font-semibold font-Montserrat leading-tight text-white">
                      {name}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-start items-center gap-5">
                <PaginationSvg
                  onClick={() => {
                    dispatch(
                      SET_PAGINATION_TYPE({ paginationType: paginationType === 'infinite' ? 'paginated' : 'infinite' })
                    )
                    if (paginationType === 'infinite') {
                      track('dealer_set_manual_pagination')
                    } else {
                      track('dealer_set_infinite_pagination')
                    }
                  }}
                  className="w-9 h-6 cursor-pointer"
                  style={{ color: paginationType === 'paginated' ? COLORS.lightBlue : COLORS.white }}
                />
                <LayoutTwoColumnSvg
                  onClick={() => dispatch(SET_COLUMN_NUMBER({ columns: 'less' }))}
                  className="w-6 h-6 cursor-pointer"
                  style={{ color: columns === 'less' ? COLORS.white : COLORS.lightBlue }}
                />
                <LayoutThreeColumnSvg
                  onClick={() => dispatch(SET_COLUMN_NUMBER({ columns: 'more' }))}
                  className="w-6 h-6 cursor-pointer"
                  style={{ color: columns === 'more' ? COLORS.white : COLORS.lightBlue }}
                />
                <Link
                  to="/browsing/liked"
                  className="px-3 py-2 bg-Estigroup-primary rounded-md flex justify-center items-center gap-1.5 overflow-hidden ml-3"
                  style={{
                    boxShadow:
                      '0px 0px 0px 1px rgba(53,130,206,1.00), 0px 1px 2px 0px rgba(0,89,138,1.00), inset 0px 0.75px 0px 0px rgba(255,255,255,0.20)'
                  }}>
                  <Icon
                    color="white"
                    backgroundColor="groupBlue"
                    notificationsCount={1}
                    borderColor="white"
                    circleTop={-8}
                    circleRight={-8}
                    iconComponent={<LikedSvg variant="liked" />}
                  />
                  <div className="justify-start text-buttons-text-primary text-sm font-medium font-Montserrat leading-tight text-white">
                    {t('liked_vehicles')}
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
        {withFiler && (
          <div className="self-stretch h-14 p-4 bg-backgrounds-bg-component rounded-bl-lg rounded-br-lg border-l border-r border-b border-neutral-200 inline-flex justify-between items-center">
            <div className="flex justify-start items-center gap-2">
              <div className="p-1 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-200 flex justify-between items-start overflow-hidden">
                <MotorbikeFilter />
                <CamperFilter />
                <AgriculturalFilter />
                <JetSkiFilter />
              </div>
            </div>
            <div className="flex justify-start items-center gap-3">
              <BuyNowToggleSwitch />
              <UniversalSearch />
              <CountrySelector />
              <div
                className="p-2 bg-buttons-button-neutral rounded-md flex justify-center items-center"
                style={{
                  boxShadow: '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'
                }}>
                <div className="w-3.5 h-3.5 relative">
                  <Icon
                    dataTestId="filter-button"
                    iconComponent={<FilterSvg className="w-4 h-4 cursor-pointer" />}
                    notificationsCount={activeModalFiltersCount}
                    color="white"
                    backgroundColor="errorRed"
                    borderColor="white"
                    circleSize={5}
                    circleTop={-15}
                    circleRight={-15}
                    onClick={() => {
                      dispatch(TOGGLE_FILTER_MODAL())
                      track('dealer_open_filter_modal')
                    }}
                  />
                </div>
              </div>
              <div
                className="p-2 bg-buttons-button-neutral rounded-md flex justify-center items-center overflow-hidden h-8"
                style={{
                  boxShadow: '0px 0px 0px 1px rgba(0,0,0,0.08), 0px 1px 2px 0px rgba(0,0,0,0.12)'
                }}>
                <div className="w-4 h-4 relative">
                  <RefreshSvg
                    onClick={() => {
                      if (paginationType === 'infinite') {
                        dispatch(FETCH_OFFERS({ tab: currentTab, refetch: true }))
                      } else {
                        dispatch(FETCH_OFFERS_PAGINATED({ refetch: true }))
                      }
                    }}
                    className="cursor-pointer"
                    style={{
                      width: '17px',
                      height: '17px',
                      animation: isLoading ? 'spin 1s linear infinite' : 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default NewToolbar
